import gql from 'graphql-tag';
import React, {useEffect} from 'react';

import {assertAndContinue} from 'Asserts';
import CollaborationManagementSection from 'collaborator-components/CollaborationManagementSection';
import OwnableCollaboratorsSection, {
  OwnableCollaboratorsSectionProps,
} from 'collaborator-components/OwnableCollaboratorsSection';
import {
  CollaborationManagementActions,
  CollaborationManagementState,
} from 'collaborator-components/useCollaborationManagementState';
import {isOrganization, isUser, User} from 'constants/UserConstants';
import {
  ReviewLifecyclesForOrganizationQuery,
  ReviewLifecyclesForOrganizationQueryVariables,
} from 'create-components/__generated/ReviewLifecyclesForOrganizationQuery';
import {
  ReviewLifecyclesForUserQuery,
  ReviewLifecyclesForUserQueryVariables,
} from 'create-components/__generated/ReviewLifecyclesForUserQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ReviewProcessSelector, {
  generateReviewProcessSelectorOptions,
} from 'notebook/reviews/review-lifecycles/ReviewProcessSelector';
import notifications from 'notifications';
import {Organization} from 'orgs/OrganizationConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import * as logging from 'util/Logging';
import sortedBy from 'util/sortedBy';

type OrganizationGraphQL = ExtractByTypename<
  ReviewLifecyclesForOrganizationQuery['organization'],
  'OrganizationNode'
>;

type ReviewLifecycleGraphQL = OrganizationGraphQL['reviewLifecycles'][number];

interface BaseReviewLifecycleAndCollaboratorsSectionProps {
  reviewLifecycleIds?: Array<string>;
  selectReviewLifecycleId: (reviewLifecycleId: string) => void;
  removeReviewLifecycleId: (reviewLifecycleId: string) => void;
  showWarningMessageOnReviewLifecycleEdit?: boolean;
  initialReviewLifecycleIds?: Array<string>;
  isAuditorBasedReview: boolean;
  isMultiReviewProcessSelector: boolean;
  collaborationState?: CollaborationManagementState;
  collaborationActions?: CollaborationManagementActions;
  policeableName: string;
  setIsAuditorBasedReview: (isAuditorBasedReview: boolean) => void;
  canManageCollaborators: boolean;
  onRequestClose?: () => void;
}

interface ReviewLifecycleAndCollaboratorsSectionForOrganizationProps
  extends BaseReviewLifecycleAndCollaboratorsSectionProps, OwnableCollaboratorsSectionProps {
  owner: Organization;
}

interface ReviewLifecycleAndCollaboratorsSectionForUserProps
  extends BaseReviewLifecycleAndCollaboratorsSectionProps, OwnableCollaboratorsSectionProps {
  owner: User;
}

export const REVIEW_LIFECYCLES_FOR_ORGANIZATION_QUERY = gql`
  query ReviewLifecyclesForOrganizationQuery($orgId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    organization(id: $orgId) {
      ... on OrganizationNode {
        id
        name
        reviewLifecycles {
          archiveRecord {
            purpose
          }
          currentVersion {
            id
            name
          }
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const REVIEW_LIFECYCLES_FOR_USER_QUERY = gql`
  query ReviewLifecyclesForUserQuery($userId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    user(id: $userId) {
      ... on UserNode {
        id
        organizations {
          ... on OrganizationNode {
            id
            name
            reviewLifecycles {
              archiveRecord {
                purpose
              }
              currentVersion {
                id
                name
              }
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function generateReviewProcessOptionsWithOrganizationSectionHeaders(
  organizations: Array<OrganizationGraphQL>,
  selectedReviewLifecycleIds: Array<string>,
  isMulti: boolean
): Array<OptionData> {
  const orgs = sortedBy(organizations, ['name']);
  const options: Array<OptionData> = [];
  orgs.forEach(({name, reviewLifecycles}) => {
    if (reviewLifecycles.length > 0) {
      options.push({name, value: '', isHeader: true});
      options.push(
        ...generateReviewProcessSelectorOptions({
          reviewLifecycleIdAndNames: reviewLifecycles.map(({currentVersion, id}) => ({
            id,
            name: currentVersion.name,
          })),
          selectedReviewLifecycleIds,
          isMulti,
        })
      );
    }
  });
  return options;
}

function renderCollaboratorsSection(
  props: OwnableCollaboratorsSectionProps & {
    collaborationState?: CollaborationManagementState;
    collaborationActions?: CollaborationManagementActions;
    enableAuditors: boolean;
    owner: User | Organization;
    policeableName: string;
    canManageCollaborators: boolean;
    onRequestClose?: () => void;
  }
): JSX.Element {
  return props.collaborationState != null && props.collaborationActions != null ? (
    <CollaborationManagementSection
      {...props.collaborationState}
      {...props.collaborationActions}
      renderAuditorColumn={props.enableAuditors}
      policeableName={props.policeableName}
      closeParentModal={props.onRequestClose}
    />
  ) : (
    <OwnableCollaboratorsSection {...props} isReadOnly={!props.canManageCollaborators} />
  );
}

function PresentationalReviewLifecycleAndCollaboratorsSection({
  owner,
  organizationsWithReviewLifecycles,
  selectedReviewLifecycleIds,
  selectReviewLifecycleId,
  removeReviewLifecycleId,
  initialReviewLifecycleIds,
  showWarningMessageOnReviewLifecycleEdit,
  collaborationState,
  collaborationActions,
  isAuditorBasedReview,
  setIsAuditorBasedReview,
  isMultiReviewProcessSelector,
  ...ownableCollaboratorsSectionProps
}: BaseReviewLifecycleAndCollaboratorsSectionProps &
  OwnableCollaboratorsSectionProps & {
    organizationsWithReviewLifecycles: Array<OrganizationGraphQL>;
    reviewLifecycleIds?: never;
    selectedReviewLifecycleIds: Array<string>;
    owner: Organization | User;
  }): JSX.Element {
  const availableReviewLifecycles = organizationsWithReviewLifecycles
    .flatMap<ReviewLifecycleGraphQL>((organization) => organization.reviewLifecycles)
    .filter((rlc) => rlc.archiveRecord == null);

  // If the owner is an organization without review lifecycles, then it must be legacy (auditor based) review.
  // But if isAuditorBasedReview is set to false for whatever reason, the parent component may be incorrectly
  // rendering that it's not legacy review. We override isAuditorBasedReview to be true in this case.
  useEffect(() => {
    if (availableReviewLifecycles.length === 0 && !isAuditorBasedReview) {
      setIsAuditorBasedReview(true);
    }
  }, [availableReviewLifecycles.length, isAuditorBasedReview, setIsAuditorBasedReview]);

  return (
    <>
      {availableReviewLifecycles.length > 0 && (
        <ReviewProcessSelector
          includeAuditorBasedReviewOption={true}
          isAuditorBasedReview={isAuditorBasedReview}
          setIsAuditorBasedReview={setIsAuditorBasedReview}
          isRequired={true}
          isMulti={isMultiReviewProcessSelector}
          showWarnings={true}
          className="well"
          options={
            organizationsWithReviewLifecycles.length > 1
              ? generateReviewProcessOptionsWithOrganizationSectionHeaders(
                  organizationsWithReviewLifecycles,
                  selectedReviewLifecycleIds,
                  isMultiReviewProcessSelector
                )
              : generateReviewProcessSelectorOptions({
                  reviewLifecycleIdAndNames: availableReviewLifecycles.map(({currentVersion, id}) => ({
                    id,
                    name: currentVersion.name,
                  })),
                  selectedReviewLifecycleIds,
                  isMulti: isMultiReviewProcessSelector,
                })
          }
          selectedReviewLifecycleIds={selectedReviewLifecycleIds ?? []}
          selectReviewLifecycleId={selectReviewLifecycleId}
          removeReviewLifecycleId={removeReviewLifecycleId}
          initialReviewLifecycleIds={initialReviewLifecycleIds}
          showWarningMessageOnReviewLifecycleEdit={showWarningMessageOnReviewLifecycleEdit}
          isReadOnly={ownableCollaboratorsSectionProps.isReadOnly}
          collaborators={ownableCollaboratorsSectionProps.collaborators}
          projectOwnerId={owner.id}
          ownerInfo={ownableCollaboratorsSectionProps.ownerInfo}
        />
      )}
      {renderCollaboratorsSection({
        collaborationState,
        collaborationActions,
        ...ownableCollaboratorsSectionProps,
        enableAuditors: isAuditorBasedReview,
        owner,
      })}
    </>
  );
}

function ReviewLifecycleAndCollaboratorsSectionForUser({
  owner,
  reviewLifecycleIds: selectedReviewLifecycleIds,
  selectReviewLifecycleId,
  removeReviewLifecycleId,
  initialReviewLifecycleIds,
  isMultiReviewProcessSelector,
  showWarningMessageOnReviewLifecycleEdit,
  isAuditorBasedReview,
  setIsAuditorBasedReview,
  collaborationState,
  collaborationActions,
  policeableName,
  canManageCollaborators,
  ...ownableCollaboratorsSectionProps
}: ReviewLifecycleAndCollaboratorsSectionForUserProps): JSX.Element {
  const result = useBenchlingQuery<ReviewLifecyclesForUserQuery, ReviewLifecyclesForUserQueryVariables>(
    REVIEW_LIFECYCLES_FOR_USER_QUERY,
    {variables: {userId: owner.id}}
  );

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('There was an error loading data. Please refresh the page');
    return renderCollaboratorsSection({
      collaborationState,
      collaborationActions,
      ...ownableCollaboratorsSectionProps,
      enableAuditors: true,
      owner,
      policeableName,
      canManageCollaborators,
    });
  }
  const {user} = result.data;

  if (isInaccessible(user)) {
    logging.error(
      `User was unable to somehow query for their own data in Ownable Settings Modal when fetching review lifecycle data`
    );
    return renderCollaboratorsSection({
      collaborationState,
      collaborationActions,
      ...ownableCollaboratorsSectionProps,
      enableAuditors: true,
      owner,
      policeableName,
      canManageCollaborators,
    });
  } else {
    return (
      <PresentationalReviewLifecycleAndCollaboratorsSection
        owner={owner}
        organizationsWithReviewLifecycles={user.organizations}
        selectedReviewLifecycleIds={selectedReviewLifecycleIds ?? []}
        selectReviewLifecycleId={selectReviewLifecycleId}
        removeReviewLifecycleId={removeReviewLifecycleId}
        initialReviewLifecycleIds={initialReviewLifecycleIds}
        showWarningMessageOnReviewLifecycleEdit={showWarningMessageOnReviewLifecycleEdit}
        collaborationState={collaborationState}
        collaborationActions={collaborationActions}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        policeableName={policeableName}
        isMultiReviewProcessSelector={isMultiReviewProcessSelector}
        canManageCollaborators={canManageCollaborators}
        {...ownableCollaboratorsSectionProps}
      />
    );
  }
}

function ReviewLifecycleAndCollaboratorsSectionForOrganization({
  owner,
  reviewLifecycleIds: selectedReviewLifecycleIds,
  selectReviewLifecycleId,
  removeReviewLifecycleId,
  initialReviewLifecycleIds,
  showWarningMessageOnReviewLifecycleEdit,
  collaborationState,
  collaborationActions,
  policeableName,
  isAuditorBasedReview,
  setIsAuditorBasedReview,
  isMultiReviewProcessSelector,
  ...ownableCollaboratorsSectionProps
}: ReviewLifecycleAndCollaboratorsSectionForOrganizationProps): JSX.Element {
  const result = useBenchlingQuery<
    ReviewLifecyclesForOrganizationQuery,
    ReviewLifecyclesForOrganizationQueryVariables
  >(REVIEW_LIFECYCLES_FOR_ORGANIZATION_QUERY, {variables: {orgId: owner.id}});

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    notifications.error('There was an error loading data. Please refresh the page');
    return renderCollaboratorsSection({
      collaborationState,
      collaborationActions,
      ...ownableCollaboratorsSectionProps,
      enableAuditors: true,
      owner,
      policeableName,
    });
  }
  const {organization} = result.data;
  if (isInaccessible(organization)) {
    logging.error(
      `User was unable to somehow query for their own organization's data in Ownable Settings Modal when fetching review lifecycle data`
    );
    return renderCollaboratorsSection({
      collaborationState,
      collaborationActions,
      ...ownableCollaboratorsSectionProps,
      enableAuditors: true,
      owner,
      policeableName,
    });
  }

  return (
    <PresentationalReviewLifecycleAndCollaboratorsSection
      owner={owner}
      organizationsWithReviewLifecycles={[organization]}
      selectedReviewLifecycleIds={selectedReviewLifecycleIds ?? []}
      selectReviewLifecycleId={selectReviewLifecycleId}
      removeReviewLifecycleId={removeReviewLifecycleId}
      initialReviewLifecycleIds={initialReviewLifecycleIds}
      showWarningMessageOnReviewLifecycleEdit={showWarningMessageOnReviewLifecycleEdit}
      collaborationState={collaborationState}
      collaborationActions={collaborationActions}
      isAuditorBasedReview={isAuditorBasedReview}
      setIsAuditorBasedReview={setIsAuditorBasedReview}
      policeableName={policeableName}
      isMultiReviewProcessSelector={isMultiReviewProcessSelector}
      {...ownableCollaboratorsSectionProps}
    />
  );
}

export type ReviewLifecycleAndCollaboratorsSectionProps = OwnableCollaboratorsSectionProps &
  BaseReviewLifecycleAndCollaboratorsSectionProps;

export default function ReviewLifecycleAndCollaboratorsSection({
  owner,
  selectReviewLifecycleId: onReviewLifecycleChange,
  reviewLifecycleIds,
  showWarningMessageOnReviewLifecycleEdit,
  isAuditorBasedReview,
  setIsAuditorBasedReview,
  isMultiReviewProcessSelector,
  collaborators,
  collaborationActions,
  collaborationState,
  policeableName,
  canManageCollaborators,
  ...ownableCollaboratorsSectionProps
}: ReviewLifecycleAndCollaboratorsSectionProps): JSX.Element | null {
  if (isUser(owner)) {
    return (
      <ReviewLifecycleAndCollaboratorsSectionForUser
        owner={owner}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        reviewLifecycleIds={reviewLifecycleIds}
        selectReviewLifecycleId={onReviewLifecycleChange}
        showWarningMessageOnReviewLifecycleEdit={showWarningMessageOnReviewLifecycleEdit}
        isMultiReviewProcessSelector={isMultiReviewProcessSelector}
        collaborators={collaborators}
        collaborationActions={collaborationActions}
        collaborationState={collaborationState}
        policeableName={policeableName}
        canManageCollaborators={canManageCollaborators}
        {...ownableCollaboratorsSectionProps}
      />
    );
  } else if (isOrganization(owner)) {
    return (
      <ReviewLifecycleAndCollaboratorsSectionForOrganization
        owner={owner}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        reviewLifecycleIds={reviewLifecycleIds}
        selectReviewLifecycleId={onReviewLifecycleChange}
        showWarningMessageOnReviewLifecycleEdit={showWarningMessageOnReviewLifecycleEdit}
        isMultiReviewProcessSelector={isMultiReviewProcessSelector}
        collaborators={collaborators}
        collaborationActions={collaborationActions}
        collaborationState={collaborationState}
        policeableName={policeableName}
        canManageCollaborators={canManageCollaborators}
        {...ownableCollaboratorsSectionProps}
      />
    );
  } else {
    assertAndContinue(false, 'Somehow tried to render a Benchling App owned Settings modal');
    return null;
  }
}
